import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { format } from 'date-fns';
import {
  GetActiveUniqueProfilesMetricsQuery,
  GetProfileListV2Query,
  GetProfileListV2QueryVariables
} from '@/gql/graphql';
import { CopyableText } from '@/components/ui/copyable-text';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { useFilterPage, useProfileFilters, useRefetchLastDate } from '../store/store';
import { useQuery } from '@apollo/client';
import { GET_V2_PROFILES } from '../queries/profiles';
import { Skeleton } from '@/components/ui/skeleton';
import { TablePagination } from './table-pagination';
import { useMemo, useCallback, useEffect } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { GET_ACTIVE_UNIQUE_PROFILE_METRICS } from '../queries/metrics';
import { formatPhoneNumber } from '@contextual/common/utils';

type Profile = GetProfileListV2Query['listProfiles'][number];

const columns: ColumnDef<Profile>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => {
      const { firstName, lastName } = row.original;
      const name = `${firstName ?? ''} ${lastName ?? ''}`.trim();
      return (
        <div className="text-left">
          <CopyableText text={typeof name === 'string' ? name : null} />
        </div>
      );
    }
  },
  {
    accessorKey: 'email',
    header: 'Email',
    cell: ({ row }) => {
      const email = row.getValue('email');
      return (
        <div className="text-left">
          <CopyableText text={typeof email === 'string' ? email : null} />
        </div>
      );
    }
  },
  {
    accessorKey: 'phone',
    header: 'Phone',
    cell: ({ row }) => {
      const phone = row.getValue('phone');
      return (
        <div className="text-left">
          <CopyableText text={typeof phone === 'string' ? formatPhoneNumber(phone) : null} />
        </div>
      );
    }
  },
  {
    accessorKey: 'firstSeen',
    header: 'First Seen',
    cell: ({ row }) => {
      const firstSeen = row.getValue('firstSeen');
      return (
        <div className="flex w-full">
          {firstSeen ? format(new Date(firstSeen as string), 'MMM d, yyyy') : ''}
        </div>
      );
    }
  },
  {
    accessorKey: 'lastSeen',
    header: 'Last Seen',
    cell: ({ row }) => {
      const lastSeen = row.getValue('lastSeen');
      return (
        <div className="flex w-full">
          {lastSeen ? format(new Date(lastSeen as string), 'MMM d, yyyy') : ''}
        </div>
      );
    }
  }
];

export function ProfilesTableData() {
  const filters = useProfileFilters();
  const { pageIndex, pageSize } = useFilterPage();
  const refetchLastDate = useRefetchLastDate();

  const queryFilters = useCallback((): GetProfileListV2QueryVariables | undefined => {
    if (filters.type !== 'valid') {
      return undefined;
    }

    const { dateRange, searchTerm } = filters;
    return {
      filters: {
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString(),
        search: searchTerm !== '' ? searchTerm : undefined
      },
      pagination: {
        pageNumber: pageIndex - 1,
        pageSize: pageSize
      }
    };
  }, [filters, pageIndex, pageSize]);

  const {
    data: profilesData,
    loading: profilesLoading,
    error,
    refetch: ProfilesRefetch
  } = useQuery<GetProfileListV2Query>(GET_V2_PROFILES, {
    variables: queryFilters(),
    skip: filters.type !== 'valid',
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true
  });

  const {
    data: metricsData,
    loading: metricsLoading,
    refetch: MetricsRefetch
  } = useQuery<GetActiveUniqueProfilesMetricsQuery>(GET_ACTIVE_UNIQUE_PROFILE_METRICS, {
    variables: queryFilters(),
    skip: filters.type !== 'valid'
  });

  useEffect(() => {
    void ProfilesRefetch();
    void MetricsRefetch();
  }, [refetchLastDate, ProfilesRefetch, MetricsRefetch]);

  const tableData = useMemo(() => profilesData?.listProfiles ?? [], [profilesData?.listProfiles]);
  const totalCount = useMemo(
    () => metricsData?.getActiveUniqueProfileMetrics.totalProfiles ?? 0,
    [metricsData?.getActiveUniqueProfileMetrics.totalProfiles]
  );

  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    initialState: {
      pagination: {
        pageSize: pageSize,
        pageIndex: pageIndex
      }
    }
  });

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Error loading profiles: {error.message}</AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <div className="rounded-md border bg-card">
        <Table className="rounded-lg">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {profilesLoading || metricsLoading ? (
              <ProfileTableLoading />
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="h-12">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <TablePagination table={table} totalCount={totalCount} />
    </>
  );
}

function ProfileTableLoading() {
  return (
    <>
      {Array.from({ length: 10 }).map((_, i) => (
        <TableRow key={i} className="h-12">
          {columns.map((_, j) => (
            <TableCell key={j} className="py-3">
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}
